/* eslint-disable no-empty-pattern */
import {
  ButtonMenu,
  column,
  Css,
  GridColumn,
  GridDataRow,
  GridSortConfig,
  GridTable,
  Kinded,
  RowStyles,
  ScrollableContent,
  simpleDataRows,
  SimpleHeaderAndData,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import { LotsPageLotFragment, LotsPageLotsQuery, useLotsPageLotsQuery } from "src/generated/graphql-types";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { createLotDetailsUrl } from "src/RouteUrls";
import { formatNumberToString } from "src/utils";
import { queryResult } from "src/utils/queryResult";
import { PageHeader } from "../layout/PageHeader";
import { TableActions } from "../layout/TableActions";
import { SearchBox } from "src/components/SearchBox";

export function LotsPage() {
  useDocumentTitle("Lots");

  // Hacktastic/Placeholder until we need to do an earlier user query
  return LotsDataView({});
}

type LotsDataViewProps = Record<string, never>;

export function LotsDataView({}: LotsDataViewProps) {
  const query = useLotsPageLotsQuery({ variables: {} });
  const [searchFilter, setSearchFilter] = useState<string | undefined>();
  const initSortState: GridSortConfig = useMemo(() => ({ on: "client", initial: ["1", "ASC"] }), []);

  return queryResult(query, (data) => (
    <div css={Css.mr2.$}>
      <PageHeader title="Lots" />
      <TableActions onlyLeft>
        <SearchBox onSearch={setSearchFilter} />
      </TableActions>
      <ScrollableContent virtualized>
        <GridTable
          stickyHeader
          sorting={initSortState}
          fallbackMessage="No lots found."
          columns={columns}
          rows={createRows(data)}
          rowStyles={rowStyles}
          filter={searchFilter}
          style={{ rowHeight: "fixed" }}
          as="virtual"
        />
      </ScrollableContent>
    </div>
  ));
}

type Row = SimpleHeaderAndData<LotsPageLotFragment> & Kinded;

const columns: GridColumn<Row>[] = [
  column<Row>({ header: "ID", data: (data) => ({ content: data.id }), w: "72px" }),
  column<Row>({
    header: "Address",
    data: (data) => data.address?.street1,
    w: "272px",
  }),
  column<Row>({
    header: "Investment Status",
    data: (data) => data.lotInvestmentStatus.name,
    w: "172px",
  }),
  column<Row>({
    header: "UW Investor Margin",
    data: (data) => formattedPercentage(data.underwrittenInvestorMarginBasisPoints / 100),
    w: "132px",
  }),
  column<Row>({
    header: "Current Investor Margin",
    data: (data) => formattedPercentage(data.investorMarginBasisPoints / 100),
    w: "172px",
  }),
  column<Row>({
    header: "SalesForce ID",
    data: (data) => data.salesforceOpportunityId,
    w: "172px",
  }),
  column<Row>({
    header: "Facility",
    data: (data) => data.fund?.name,
    w: "172px",
  }),
  column<Row>({
    header: "Legal Entity",
    data: (data) => data.legalEntityName ?? emptyCellDash,
    w: "200px",
  }),
  column<Row>({ header: "UW dpid", data: (data) => data.dpid, w: "172px" }),
];

const emptyCellDash = "-";

function formattedPercentage(percent: number) {
  const formattedPercentage = typeof percent !== "number" ? emptyCellDash : `${formatNumberToString(percent, true)}%`;
  return formattedPercentage;
}

function createRows(data: LotsPageLotsQuery): GridDataRow<Row>[] {
  return simpleDataRows(data.lots);
}

const rowStyles: RowStyles<Row> = {
  header: {},
  data: { rowLink: ({ id }) => createLotDetailsUrl(id) },
};
